import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import BaseLink from '~/components/base/BaseLink';
import Icon from '~/components/base/Icon';

import BottomMobileFooter from '~/components/footer/BottomMobileFooter';

import { HiddenSpan, calcVw } from '~/styles/helpers';

import useOnKeyPress from '~/hooks/useOnKeyPress';
import { isBrowser, formatText } from '~/utilities/helpers';

const Wrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: ${calcVw(100)};
    height: 100vh;
    z-index: ${({ theme }) => theme.zIndex.footerChildMenu};
    background: ${({ theme }) => theme.colors.text.blue};
    padding: 70px 30px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-size: 2.4rem;
    color: ${({ theme }) => theme.colors.primary.white};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-weight: ${({ theme }) => theme.fontWeights.black};
`;

const BackLinkWrapper = BackButton.withComponent('div');

const BackIcon = styled(Icon)`
    transform: rotate(180deg);
`;

const NavList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-left: 35px;
`;

const MobileNavChildMenu = ({
    label,
    url,
    childItems,
    handleMenu,
    socialLinks = [],
    triggerRef,
    headerRef
}) => {
    const backRef = useRef();

    // Keyboard Navigation: direct focus to navigate to/from close trigger and menu
    useOnKeyPress('Tab', (e) => {
        if (isBrowser && backRef?.current && triggerRef?.current) {
            if (e.shiftKey && document.activeElement === backRef.current) {
                e.preventDefault();
                triggerRef.current.focus();
            } else if (
                !e.shiftKey &&
                document.activeElement === triggerRef.current
            ) {
                e.preventDefault();
                backRef.current.focus();
            }
        }
    });

    return (
        <Wrapper>
            {url === '#' ? (
                <BackButton
                    type="button"
                    onClick={() => handleMenu(headerRef)}
                    ref={backRef}
                >
                    <HiddenSpan>Back to Main Navigation</HiddenSpan>
                    <BackIcon icon="arrow" color="yellow" size={23} />
                    <h2 id={`SubHeading-${label}`}>{formatText(label)}</h2>
                </BackButton>
            ) : (
                <BackLinkWrapper>
                    <BackButton
                        type="button"
                        onClick={() => handleMenu(headerRef)}
                        ref={backRef}
                    >
                        <HiddenSpan>Back to Main Navigation</HiddenSpan>
                        <BackIcon icon="arrow" color="yellow" size={23} />
                    </BackButton>
                    <BaseLink to={url}>
                        <h2 id={`SubHeading-${label}`}>{formatText(label)}</h2>
                    </BaseLink>
                </BackLinkWrapper>
            )}

            <NavList aria-labelledby={`SubHeading-${label}`}>
                {childItems.nodes.map(({ url, label, id }) => (
                    <li key={id}>
                        <BaseLink to={url}>{formatText(label)}</BaseLink>
                    </li>
                ))}
            </NavList>

            <BottomMobileFooter {...{ socialLinks }} />
        </Wrapper>
    );
};

MobileNavChildMenu.propTypes = {
    label: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    childItems: PropTypes.shape({}).isRequired,
    handleMenu: PropTypes.func.isRequired,
    socialLinks: PropTypes.arrayOf(PropTypes.shape({})),
    triggerRef: PropTypes.shape({}).isRequired,
    headerRef: PropTypes.shape({}).isRequired
};

export default MobileNavChildMenu;
